import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';
import { IconLink, IconMessageCircle } from '@tabler/icons-react';

import { Tooltip, Typography } from '@zendesk/forethought-elements';
import { VoiceTranscript } from './types';
import { getTranscriptTime } from './utils';

function stripHtmlFull(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  } catch {
    return html;
  }
}

export const WidgetTranscript = ({
  startTime,
  transcript,
}: {
  startTime: number;
  transcript: VoiceTranscript;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const timestamp = getTranscriptTime(
    startTime,
    transcript.start_timestamp || 0,
  );

  if (transcript.widget_type === 'image') {
    const images = transcript.widget_data?.images as
      | Array<{ description?: string; url: string }>
      | undefined;
    const firstImage = images?.[0];

    return (
      <Tooltip placement='top' tooltipContent={timestamp}>
        <WidgetCard>
          <Typography color={palette.colors.grey[600]} variant='font12'>
            {t('widgetTranscript.supportCenterSharedImage')}
          </Typography>
          {firstImage && (
            <ImageContainer>
              <img
                alt={
                  firstImage.description || t('widgetTranscript.sharedImageAlt')
                }
                src={decodeURIComponent(firstImage.url)}
                style={{
                  borderRadius: '8px',
                  maxHeight: '180px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </ImageContainer>
          )}
        </WidgetCard>
      </Tooltip>
    );
  }

  if (transcript.widget_type === 'text_message') {
    const message = transcript.widget_data?.message as string | undefined;
    const displayText = message ? stripHtmlFull(message) : '';

    if (!displayText) {
      return null;
    }

    return (
      <Tooltip placement='top' tooltipContent={timestamp}>
        <WidgetCard>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <IconContainer>
              <IconMessageCircle size={16} />
            </IconContainer>
            <Box>
              <Typography color={palette.colors.grey[600]} variant='font12'>
                {t('widgetTranscript.supportCenterSharedMessage')}
              </Typography>
              <Typography color={palette.colors.grey[800]} variant='font14'>
                {displayText}
              </Typography>
            </Box>
          </Box>
        </WidgetCard>
      </Tooltip>
    );
  }

  if (transcript.widget_type === 'article') {
    const articles = transcript.widget_data?.articles as
      | Array<{ link?: string; title?: string }>
      | undefined;
    const link =
      (transcript.widget_data?.link as string | undefined) ||
      articles?.[0]?.link;
    const title =
      (transcript.widget_data?.title as string | undefined) ||
      articles?.[0]?.title;

    return (
      <Tooltip placement='top' tooltipContent={timestamp}>
        <WidgetCard>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <IconContainer>
              <IconLink size={16} />
            </IconContainer>
            <Box>
              <Typography color={palette.colors.grey[600]} variant='font12'>
                {t('widgetTranscript.supportCenterSharedLink')}
              </Typography>
              {link && (
                <a
                  href={link}
                  rel='noopener noreferrer'
                  style={{
                    color: palette.colors.blue[600],
                    fontSize: '14px',
                    textDecoration: 'underline',
                  }}
                  target='_blank'
                >
                  {title || link}
                </a>
              )}
            </Box>
          </Box>
        </WidgetCard>
      </Tooltip>
    );
  }

  return null;
};

const WidgetCard = styled(Box)`
  background-color: ${props => props.theme.palette.colors.slate[100]};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
`;

const ImageContainer = styled('div')`
  border-radius: 8px;
  overflow: hidden;
`;

const IconContainer = styled('div')`
  align-items: center;
  background-color: ${props => props.theme.palette.colors.slate[100]};
  border-radius: 50%;
  display: flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  width: 32px;
`;
